import React, { useEffect, useMemo, useState } from 'react';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import FilterCustom from 'components/Tvendo/Common/FilterCustom';
import { useParams, useSearchParams } from 'react-router-dom';
import RegularButton from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import { DataGrid, esES } from '@mui/x-data-grid';
import GridItem from 'components/Grid/GridItem';
import { TextField } from '@material-ui/core';
import { wsGetAll } from 'services/WService';
import { Search } from '@material-ui/icons';
import { wsCreate } from 'services/WService';

const columns = [
  {
    field: 'DocEntry',
    width: 75,
    align: 'left',
    headerName: '#',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
  },
  {
    field: 'Tipo',
    width: 100,
    align: 'center',
    headerName: 'Tipo',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.ObjType === '13') return 'Documento';
      if (row.selectable === false) return 'NC';
      return 'NC Huerfano';
    },
  },
  {
    field: 'SeriesStr',
    width: 70,
    headerName: 'Zona',
    hideable: false,
    sortable: false,
  },
  {
    field: 'U_SYP_MDTD',
    width: 100,
    headerName: 'T.Doc Sun.',
    hideable: false,
    sortable: false,
  },
  { field: 'U_SYP_MDSD', width: 100, headerName: 'Serie Doc.', hideable: false, sortable: false },
  { field: 'U_SYP_MDCD', width: 100, headerName: 'Corr. Sun.', hideable: false, sortable: false },
  { field: 'DocNum', width: 100, headerName: 'Num. SAP', hideable: false, sortable: false },
  {
    field: 'DocStatus',
    width: 100,
    headerName: 'Esta Doc.',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
  },
  { field: 'TaxDate', width: 100, headerName: 'Fec. Doc.', hideable: false, sortable: false },
  { field: 'DocDueDate', width: 100, headerName: 'Fec. Vto.', hideable: false, sortable: false },
  { field: 'backDay', width: 100, headerName: 'Dias atrás', hideable: false, sortable: false },
  {
    field: 'DiscPrcnt',
    width: 70,
    align: 'right',
    headerName: '%Dsct.',
    hideable: false,
    sortable: false,
  },
  {
    field: 'DocTotal',
    width: 100,
    align: 'right',
    headerName: 'Total Doc.',
    hideable: false,
    sortable: false,
  },
  {
    field: 'PaidToDate',
    width: 100,
    align: 'right',
    headerName: 'Pagado',
    hideable: false,
    sortable: false,
  },
  {
    field: 'Saldo',
    width: 100,
    align: 'right',
    headerName: 'Saldo',
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      return (row.DocTotal - row.PaidToDate).toFixed(2);
    },
  },
];

function ApplicateNC() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [customer, setCustomerSelect] = useState();
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typeState, setTypeState] = useState('');
  const [filter, setFilter] = useState({
    start_date: searchParams.get('start_date') || undefined,
    end_date: searchParams.get('end_date') || undefined,
    type: 'no_aplicados',
  });

  const [documents, setDocuments] = useState([]);
  const [selectionIds, setSelectionIds] = useState([]);

  const { totalDocuments, totalCreditNotes, saldo } = useMemo(() => {
    let data = documents.reduce(
      (prev, current) => {
        if (selectionIds.includes(`${current.ObjType}-${current.DocEntry}`)) {
          if (current.ObjType === '13') {
            prev.totalDocuments += current.DocTotal - current.PaidToDate;
          }
          if (current.ObjType === '14') {
            prev.totalCreditNotes += current.DocTotal - current.PaidToDate;
          }
        }
        return prev;
      },
      {
        totalDocuments: 0,
        totalCreditNotes: 0,
      },
    );
    data.totalDocuments = data.totalDocuments.toFixed(2);
    data.totalCreditNotes = data.totalCreditNotes.toFixed(2);
    data.saldo = (data.totalDocuments - data.totalCreditNotes).toFixed(2);
    return data;
  }, [selectionIds]);

  async function getCustomer() {
    let res = await wsGetAll(`base/business-partner?CardCode=${id}`);
    if (res.success) {
      if (res.data.length > 0) {
        setCustomerSelect(res.data[0]);
        return;
      }
    }
    alert('Socio de negocio no encontrado');
  }

  async function listCreditNotes() {
    let queryString = `?CardCode=${id}&start_date=${filter.start_date}&end_date=${filter.end_date}&type=${filter.type}`;
    if (!!filter.base_zone_id) {
      queryString += `&base_zone_id=${filter.base_zone_id}`;
    }
    setSelectionIds([]);
    setDocuments([]);
    setLoading(true);
    let res = await wsGetAll(`pymt/received-payment-cn${queryString}`);
    setLoading(false);
    setTypeState(filter.type);
    if (res.success) {
      let newDocuments = [...res.data];
      for (let doc of res.data) {
        if (!('CreditNotes' in doc)) continue;
        if (doc.CreditNotes.length > 0) {
          let index = newDocuments.findIndex(
            (oinv) => oinv.DocEntry === doc.DocEntry && oinv.ObjType === doc.ObjType,
          );
          newDocuments.splice(
            index + 1,
            0,
            ...doc.CreditNotes.map((item) => ({ ...item, selectable: false })),
          );
        }
      }
      setDocuments(newDocuments);
    }
  }

  function onSelectionModelChange(model) {
    const selectedIDs = new Set(model);
    const selectedRowData = documents.filter((row) =>
      selectedIDs.has(`${row.ObjType}-${row.DocEntry}`),
    );

    let ids = [];
    for (let row of selectedRowData) {
      if (row.ObjType === '14' && row.selectable === false) continue;
      ids.push(`${row.ObjType}-${row.DocEntry}`);
      if (row.ObjType === '13') {
        ids = ids.concat(row.CreditNotes.map((cn) => `${cn.ObjType}-${cn.DocEntry}`));
      }
    }

    // Si se desmarca el documento y esta en huerfanos
    if (typeState === 'huerfanos') {
      if (ids.find((id) => id.startsWith('13')) === undefined) {
        setSelectionIds([]);
        return;
      }
    }

    setSelectionIds(ids);
  }

  async function processPayments() {
    let data = {};
    if (typeState === 'no_aplicados') {
      let docs = documents.filter(
        (doc) => doc.ObjType === '13' && selectionIds.includes(`${doc.ObjType}-${doc.DocEntry}`),
      );
      data = docs.map((doc) => {
        return {
          DocEntry: doc.DocEntry,
          ObjType: doc.ObjType,
          CreditNotes: doc.CreditNotesAgg.split(','),
        };
      });
    } else if (typeState === 'huerfanos') {
      let doc = documents.find(
        (doc) => doc.ObjType === '13' && selectionIds.includes(`${doc.ObjType}-${doc.DocEntry}`),
      );
      let CreditNotes = documents
        .filter(
          (doc) => doc.ObjType === '14' && selectionIds.includes(`${doc.ObjType}-${doc.DocEntry}`),
        )
        .map((doc) => doc.DocEntry);
      data = [
        {
          DocEntry: doc.DocEntry,
          ObjType: doc.ObjType,
          CreditNotes,
        },
      ];
    } else {
      window.alert('Error, estado inválido: ' + typeState);
      return;
    }

    setProcessing(true);
    let res = await wsCreate('pymt/received-payment-cn', { documents: data });
    if (res.success) {
      alert(res.message);
      setSelectionIds([]);
      setDocuments([]);
      listCreditNotes();
    }
    setProcessing(false);
  }

  const [invoiceSelected, saldoFaltante] = useMemo(() => {
    if (selectionIds.length === 0) return [false, 0];

    let filtered = documents.filter((doc) => {
      return selectionIds.includes(`${doc.ObjType}-${doc.DocEntry}`);
    });

    let invoiceSelected = filtered.some((doc) => doc.ObjType === '13');

    let saldoFaltante = filtered.reduce((prev, doc) => {
      if (doc.ObjType === '13') {
        return prev + (doc.DocTotal - doc.PaidToDate);
      }
      if (doc.ObjType === '14') {
        return prev - (doc.DocTotal - doc.PaidToDate);
      }
      return prev;
    }, 0);

    return [invoiceSelected, saldoFaltante];
  }, [selectionIds]);

  useEffect(() => {
    getCustomer();
    listCreditNotes();
  }, []);

  return (
    <CardContainer>
      <GridContainer>
        <GridItem xs={2}>
          <div style={{ display: 'flex' }}>
            <RegularButton
              justIcon
              color="primary"
              onClick={listCreditNotes}
              disabled={processing || !customer?.CardCode}
            >
              <Search />
            </RegularButton>
            <TextField
              fullWidth
              margin="normal"
              label="Codigo cliente"
              value={customer?.CardName || ''}
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
        </GridItem>
        <GridItem xs={8}>
          <FilterCustom
            zone
            dateRange
            getFilter={setFilter}
            state
            nameState="type"
            optionValues={['aplicados', 'no_aplicados', 'huerfanos', 'sin_nc', 'todo']}
            nameOptions={['Aplicados', 'No aplicados', 'Huerfanos', 'Sin Nota Crédito', 'Todo']}
            defaultState="no_aplicados"
            defaultValues={{
              start_date: searchParams.get('start_date'),
              end_date: searchParams.get('end_date'),
            }}
          />
        </GridItem>
        <GridItem xs={1}>
          <RegularButton
            color="success"
            onClick={processPayments}
            disabled={
              processing ||
              (typeState !== 'no_aplicados' && typeState !== 'huerfanos') ||
              selectionIds.length === 0
            }
          >
            Procesar
          </RegularButton>
        </GridItem>
      </GridContainer>
      <GridContainer style={{ height: '600px' }}>
        <DataGrid
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          checkboxSelection
          rowHeight={35}
          columns={columns}
          loading={loading}
          rows={documents}
          getRowId={(row) => {
            return `${row.ObjType}-${row.DocEntry}`;
          }}
          selectionModel={selectionIds}
          isRowSelectable={({ row }) => {
            if (row.selectable === false) return false;

            if (typeState === 'huerfanos' && row.ObjType === '13') {
              // Si ya hay un documento seleccionado
              if (invoiceSelected) {
                if (!selectionIds.includes(`${row.ObjType}-${row.DocEntry}`)) return false;
              }
              return true;
            }

            if (row.ObjType === '14') {
              // Si hay al menos un documento seleccionado y esta NC no supera el total
              // Se puede seleccionar
              if (!invoiceSelected) return false;
              if (row.DocTotal - row.PaidToDate <= saldoFaltante) {
                return true;
              }
              return false;
            }
            return true;
          }}
          onSelectionModelChange={onSelectionModelChange}
          disableSelectionOnClick
        />
      </GridContainer>
      <GridContainer>
        <GridItem xs={4}>Total suma documentos: S/{totalDocuments}</GridItem>
        <GridItem xs={4}>Total suma notas crédito: S/{totalCreditNotes}</GridItem>
        <GridItem xs={4}>Diferencia: S/{saldo}</GridItem>
      </GridContainer>
    </CardContainer>
  );
}

export default ApplicateNC;
